import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileJson, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Barre Export/Import réutilisable pour tous les onglets administrables.
 *
 * Props :
 *   - collection : slug whitelisté (ex: "tarifs_types_maitres")
 *   - label      : libellé pour les toasts (ex: "Types Maîtres")
 *   - onImported : callback optionnel pour recharger les données après import
 *   - className  : classes additionnelles
 */
const ExportImportBar = ({ collection, label, onImported, className = '' }) => {
  const { getAuthHeader } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState('upsert');
  const fileInputRef = useRef(null);

  const triggerDownload = async (format) => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API}/admin/generic/${collection}/export?format=${format}`,
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${collection}_${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch {
      toast.error("Échec de l'export");
    } finally {
      setExporting(false);
    }
  };

  const onFileSelected = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(
        `${API}/admin/generic/${collection}/import?mode=${importMode}`,
        form,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } },
      );
      const { inserted = 0, updated = 0, skipped = 0, total_rows = 0 } = res.data || {};
      toast.success(
        `${label || collection} : ${inserted} inséré(s), ${updated} mis à jour, ${skipped} ignoré(s) (sur ${total_rows})`,
      );
      setShowImportModal(false);
      onImported?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Échec import';
      toast.error(typeof msg === 'string' ? msg : 'Échec import');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div className={`inline-flex items-center gap-2 ${className}`} data-testid={`export-import-bar-${collection}`}>
        <Button
          size="sm"
          variant="outline"
          disabled={exporting}
          onClick={() => triggerDownload('csv')}
          data-testid={`export-csv-${collection}`}
          title="Exporter en CSV (Excel-compatible)"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />}
          Export CSV
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={exporting}
          onClick={() => triggerDownload('json')}
          data-testid={`export-json-${collection}`}
          title="Exporter en JSON"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileJson className="w-4 h-4 mr-1.5" />}
          Export JSON
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowImportModal(true)}
          data-testid={`open-import-${collection}`}
          title="Importer un fichier CSV ou JSON"
        >
          <Upload className="w-4 h-4 mr-1.5" />
          Importer
        </Button>
      </div>

      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-md" data-testid={`import-modal-${collection}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-slate-700" />
              Importer — {label || collection}
            </DialogTitle>
            <DialogDescription>
              Sélectionnez un fichier .csv ou .json. Les colonnes doivent correspondre au schéma de la collection.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Mode d'import
              </label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger className="h-10 mt-1" data-testid={`import-mode-${collection}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upsert">
                    <div className="flex flex-col">
                      <span className="font-medium">Upsert (recommandé)</span>
                      <span className="text-xs text-slate-500">Met à jour par id, sinon crée</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="append">
                    <div className="flex flex-col">
                      <span className="font-medium">Append</span>
                      <span className="text-xs text-slate-500">Insère sans écraser (doublons ignorés)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="replace">
                    <div className="flex flex-col">
                      <span className="font-medium text-red-700">Replace (dangereux)</span>
                      <span className="text-xs text-red-600">Supprime tout avant d'importer</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.json,application/json,text/csv"
              onChange={onFileSelected}
              disabled={importing}
              className="block w-full text-sm text-slate-700
                         file:mr-3 file:py-2 file:px-4 file:rounded-md
                         file:border-0 file:text-sm file:font-semibold
                         file:bg-slate-800 file:text-white hover:file:bg-slate-700
                         disabled:opacity-50 cursor-pointer"
              data-testid={`import-file-input-${collection}`}
            />

            {importMode === 'replace' && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                ⚠️ Mode <strong>Replace</strong> : la collection sera entièrement vidée avant l'import.
                Pensez à exporter une sauvegarde au préalable.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)} disabled={importing}>
              Annuler
            </Button>
            {importing && (
              <Button disabled>
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                Import en cours…
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExportImportBar;
